import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 10px;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
  text-align: center;
`;

const TeamInfo = styled.div`
  background: #f7f7f7;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
  max-height: 300px;
  overflow-y: auto;
`;

const TeamName = styled.h3`
  color: #764ba2;
  margin-bottom: 15px;
`;

const Player = styled.div`
  padding: 10px;
  background: white;
  border-radius: 5px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CallButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 20px;
  border-radius: 50px;
  border: none;
  background: ${props => props.$active ? '#ef4444' : '#667eea'};
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.05);
    background: ${props => props.$active ? '#dc2626' : '#764ba2'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: scale(1);
  }
`;

const Status = styled.div<{ $type?: 'success' | 'error' | 'info' }>`
  padding: 15px;
  border-radius: 10px;
  margin-top: 20px;
  text-align: center;
  background: ${props => 
    props.$type === 'error' ? '#fee' : 
    props.$type === 'success' ? '#efe' : 
    '#eef'
  };
  color: ${props => 
    props.$type === 'error' ? '#c00' : 
    props.$type === 'success' ? '#060' : 
    '#006'
  };
`;

const MuteButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #333;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.1);
    background: #555;
  }
`;

// Test team data provided
const TEST_TEAM_DATA = {
  user_team: {
    teamId: "7",
    name: "Reboot",
    roster: [
      {
        name: "Joe Burrow",
        position: "QB",
        status: "starter",
        team: "Cincinnati Bengals",
        number: 9,
        week1Analysis: "Burrow begins 2025 healthy and faces Tennessee at home. The Titans have a new-look defense with rookie QB Cam Ward on the other side, which could lead to a pass-heavy game script for Cincinnati. With Ja'Marr Chase and Tee Higgins forming the league's best WR duo, Burrow should dominate. Lock him in as a top-6 QB with 25-28 point projection in this favorable matchup."
      },
      {
        name: "Drake London",
        position: "WR",
        status: "starter",
        team: "Atlanta Falcons",
        number: 5,
        week1Analysis: "Drake London begins the season as Atlanta's clear WR1 with a new quarterback under center. His imposing size and catch radius make him a reliable target, especially in the red zone where his 6'4 frame creates significant advantages."
      }
    ]
  }
};

export default function TestCallPage() {
  const [callState, setCallState] = useState<'idle' | 'connecting' | 'connected' | 'ended'>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const retellClientRef = useRef<any>(null);

  useEffect(() => {
    // Load Retell SDK
    const loadRetellSDK = async () => {
      try {
        const { RetellWebClient } = await import('retell-client-js-sdk');
        retellClientRef.current = new RetellWebClient();
        
        // Setup event listeners
        retellClientRef.current.on('call_started', () => {
          console.log('Call started');
          setCallState('connected');
        });
        
        retellClientRef.current.on('call_ended', () => {
          console.log('Call ended');
          setCallState('ended');
        });
        
        retellClientRef.current.on('error', (err: any) => {
          console.error('Retell error:', err);
          setError(err.message || 'Call error occurred');
          setCallState('idle');
        });
      } catch (err) {
        console.error('Failed to load Retell SDK:', err);
        setError('Failed to initialize call system');
      }
    };
    
    loadRetellSDK();
  }, []);

  const startCall = async () => {
    try {
      setCallState('connecting');
      setError(null);
      
      // Get microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      
      // Use the API key directly since we're in development
      const RETELL_API_KEY = 'key_2ebddbc28420903e0117a17a4a95';
      const RETELL_API_URL = 'https://api.retellai.com';
      
      // Prepare the request body for Retell API
      const requestBody = {
        agent_id: "agent_5e1d9c51e491cb9fb8efcd14e7", // Sal's agent ID (corrected)
        retell_llm_dynamic_variables: {
          caller_name: "Zachary Costa",
          team_info: JSON.stringify(TEST_TEAM_DATA),
          has_team_info: "true", // Must be string
          selected_host: "sal"
        },
        // metadata: {
        //   synth_conversation_id: `podcast_${Date.now()}`,
        //   is_training: false,
        //   is_testing: false,
        //   caller_name: "Zachary Costa"
        // }
      };
      
      console.log('Creating Retell web call with:', requestBody);
      
      // Call Retell API directly
      const response = await fetch(`${RETELL_API_URL}/v2/create-web-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${RETELL_API_KEY}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Retell API error:', errorText);
        throw new Error(`Failed to create call: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Retell API response:', data);
      
      const { access_token, call_id } = data;
      
      if (!access_token || !call_id) {
        throw new Error('Invalid response from Retell API');
      }
      
      setCallId(call_id);
      
      // Start the call
      await retellClientRef.current.startCall({
        accessToken: access_token,
        sampleRate: 24000,
        enableUpdate: true
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start call');
      setCallState('idle');
    }
  };

  const endCall = async () => {
    try {
      if (retellClientRef.current) {
        retellClientRef.current.stopCall();
      }
      setCallState('ended');
    } catch (err) {
      console.error('Error ending call:', err);
    }
  };

  const toggleMute = () => {
    if (retellClientRef.current) {
      if (isMuted) {
        retellClientRef.current.unmute();
      } else {
        retellClientRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  return (
    <Container>
      <Card>
        <Title>🏈 Test Call with Sal</Title>
        <Subtitle>Direct test call with your team data loaded</Subtitle>
        
        <TeamInfo>
          <TeamName>Team: {TEST_TEAM_DATA.user_team.name}</TeamName>
          {TEST_TEAM_DATA.user_team.roster.map((player, idx) => (
            <Player key={idx}>
              <div>
                <strong>{player.name}</strong> - {player.position}
                <br />
                <small>{player.team}</small>
              </div>
              <span>{player.status}</span>
            </Player>
          ))}
        </TeamInfo>
        
        {callState === 'idle' && (
          <CallButton onClick={startCall}>
            <Phone size={24} />
            Start Call with Sal
          </CallButton>
        )}
        
        {callState === 'connecting' && (
          <CallButton disabled>
            Connecting...
          </CallButton>
        )}
        
        {callState === 'connected' && (
          <CallButton $active onClick={endCall}>
            <PhoneOff size={24} />
            End Call
          </CallButton>
        )}
        
        {callState === 'ended' && (
          <>
            <CallButton onClick={startCall}>
              <Phone size={24} />
              Start New Call
            </CallButton>
            <Status $type="info">Call ended</Status>
          </>
        )}
        
        {error && (
          <Status $type="error">{error}</Status>
        )}
        
        {callId && (
          <Status $type="success">Call ID: {callId}</Status>
        )}
      </Card>
      
      {callState === 'connected' && (
        <MuteButton onClick={toggleMute}>
          {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
        </MuteButton>
      )}
    </Container>
  );
}